import React, { useState, useEffect, useMemo } from "react";
import Select from "react-select";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Button,
  Table,
  FormGroup,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import { getCashLedgers, exportCashLedgerToExcel, getCashLedgerById, saveCashLedger } from "../../components/Api/GmsCashLedger";
import { getBankAccounts } from "../../components/Api/GmsBankAccount";
import { getCostHeads } from "../../components/Api/GmsCostHead";
import { getEmployees } from "../../components/Api/GmsEmployee";
import { getSuppliers } from "../../components/Api/GmsSupplier";
import { getOtherPayees } from "../../components/Api/GmsOtherPayee";
import { getBanks } from "../../components/Api/GmsBank";
import { getBusinesses } from "../../components/Api/GmsBusiness";
import { getBranches } from "../../components/Api/GmsBranch";
import { checkDayClosing } from "../../components/Api/GmsDayClosing";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  hasPagePermissionWithAdmin,
  hasViewOnlyPermission,
  hasEditOrHigherPermission,
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";
import { formatDateForDisplay, formatDateForAPI } from "../../helpers/dateHelper";
import { Download, Edit } from "lucide-react";

// Custom styles for Select components
const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    borderColor: state.isFocused ? '#556ee6' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
    maxHeight: 200,
  }),
  menuList: (provided) => ({
    ...provided,
    maxHeight: 180,
    overflowY: 'auto',
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#556ee6' : state.isFocused ? '#f8f9fa' : 'white',
    color: state.isSelected ? 'white' : '#495057',
    '&:hover': {
      backgroundColor: state.isSelected ? '#556ee6' : '#e9ecef',
    },
  }),
};

const emptyFilter = {
  FromDate: null,
  ToDate: null,
  TransactorType: null,
  TransactorId: null,
  BusinessId: null,
  BranchId: null,
};

// Get user session
const getUserSession = () => {
  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  // Helper to normalize ID values
  const normalizeId = (value) => {
    if (value === null || value === undefined || value === '' || value === 'null' || value === 'undefined') {
      return null;
    }
    const numValue = typeof value === 'string' ? parseInt(value, 10) : value;
    return isNaN(numValue) || numValue <= 0 ? null : numValue;
  };

  // Prioritize authUser (latest login response)
  let businessId, branchId;

  if (authUser && Object.keys(authUser).length > 0) {
    businessId = normalizeId(authUser.companyId);
    branchId = normalizeId(authUser.branchId);
  } else {
    businessId = normalizeId(user.user_Company_Id || localStorage.getItem("userCompanyId"));
    branchId = normalizeId(user.user_Branch_Id || localStorage.getItem("userBranchId"));
  }

  return {
    userId: authUser.userId || 1,
    userName: authUser.userName || "System",
    businessId: businessId,
    branchId: branchId,
  };
};

export default function PettyCashReportPage() {
  const [cashLedgers, setCashLedgers] = useState([]);
  const [filter, setFilter] = useState(emptyFilter);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [exporting, setExporting] = useState(false);

  // Transactor data for name mapping
  const [bankAccounts, setBankAccounts] = useState([]);
  const [costHeads, setCostHeads] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [otherPayees, setOtherPayees] = useState([]);
  const [banks, setBanks] = useState([]);

  // Business and Branch state
  const [businesses, setBusinesses] = useState([]);
  const [branches, setBranches] = useState([]);
  const [filterBranches, setFilterBranches] = useState([]);
  const [loadingBusinesses, setLoadingBusinesses] = useState(false);
  const [loadingBranches, setLoadingBranches] = useState(false);

  // Edit modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [editForm, setEditForm] = useState({
    Date: new Date(),
    Amount: 0,
    TransactorType: null,
    TransactorId: null,
    CostHeadId: null,
    Notes: "",
  });
  const [selectedBank, setSelectedBank] = useState(null);
  const [bankAccountsForEdit, setBankAccountsForEdit] = useState([]);
  const [transactorOptions, setTransactorOptions] = useState([]);
  const [loadingTransactors, setLoadingTransactors] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [dayClosingStatus, setDayClosingStatus] = useState({}); // Cache day closing status for each entry

  // Permission states
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsPettyCashReport');
  const isViewOnly = hasViewOnlyPermission(permissions, '/GmsPettyCashReport');
  const hasEditOrHigher = hasEditOrHigherPermission(permissions, '/GmsPettyCashReport');

  // Get user session
  const session = useMemo(() => getUserSession(), []);

  // Transactor type options (Cost Head removed - it's now a separate field)
  const transactorTypeOptions = [
    { value: 'ba', label: 'Banks' },
    { value: 'em', label: 'Employee' },
    { value: 'su', label: 'Supplier' },
    { value: 'ot', label: 'Others' },
  ];

  useEffect(() => {
    if (hasPermission) {
      loadTransactorData();
      loadBusinesses();
      loadCashLedgers();
    } else {
      window.location.href = '/Dashboard';
    }
  }, [hasPermission, page, pageSize]);

  useEffect(() => {
    if (filter.BusinessId) {
      loadFilterBranches(filter.BusinessId);
    } else {
      setFilterBranches([]);
      setFilter(prev => ({ ...prev, BranchId: null }));
    }
  }, [filter.BusinessId]);

  // Auto-set filter business/branch from session on mount
  useEffect(() => {
    if (session.businessId && businesses.length > 0 && !filter.BusinessId) {
      setFilter(prev => ({ ...prev, BusinessId: session.businessId }));
    }
  }, [session.businessId, businesses, filter.BusinessId]);

  useEffect(() => {
    if (session.branchId && filterBranches.length > 0 && !filter.BranchId) {
      setFilter(prev => ({ ...prev, BranchId: session.branchId }));
    }
  }, [session.branchId, filterBranches, filter.BranchId]);

  const loadBusinesses = async () => {
    try {
      setLoadingBusinesses(true);
      const data = await getBusinesses({ business_Active: true });
      const businessData = Array.isArray(data) ? data : (data.data || []);
      setBusinesses(businessData);
    } catch (error) {
      console.error("Error loading businesses:", error);
    } finally {
      setLoadingBusinesses(false);
    }
  };

  const loadFilterBranches = async (businessId) => {
    if (!businessId) {
      setFilterBranches([]);
      return;
    }
    try {
      setLoadingBranches(true);
      const response = await getBranches({ Business_Id: businessId, branch_Active: true });
      const branchData = Array.isArray(response) ? response : (response.data || []);
      setFilterBranches(branchData);
    } catch (error) {
      console.error("Error loading branches:", error);
    } finally {
      setLoadingBranches(false);
    }
  };

  const loadTransactorData = async () => {
    try {
      // Load all transactor data for name mapping
      const [banksResponse, bankAccountsData, costHeadsData, employeesData, suppliersData, otherPayeesData] = await Promise.all([
        getBanks({}),
        getBankAccounts({}), // Load all bank accounts
        getCostHeads({}),
        getEmployees({}),
        getSuppliers({}),
        getOtherPayees({}),
      ]);

      // Extract arrays from response objects
      const banksArray = Array.isArray(banksResponse?.banks) ? banksResponse.banks : (Array.isArray(banksResponse) ? banksResponse : []);
      setBanks(banksArray);
      const bankAccountsArray = Array.isArray(bankAccountsData?.bankAccounts) ? bankAccountsData.bankAccounts : (Array.isArray(bankAccountsData) ? bankAccountsData : []);
      setBankAccounts(bankAccountsArray);
      setCostHeads(Array.isArray(costHeadsData) ? costHeadsData : []);
      setEmployees(Array.isArray(employeesData) ? employeesData : []);
      setSuppliers(Array.isArray(suppliersData) ? suppliersData : []);
      setOtherPayees(Array.isArray(otherPayeesData) ? otherPayeesData : []);
    } catch (error) {
      console.error("Error loading transactor data:", error);
      // Don't show error to user, just log it
    }
  };

  const loadCashLedgers = async (filterData = filter) => {
    try {
      setLoading(true);
      const filterPayload = {
        ...filterData,
        FilterType: null, // No filter - get all entries (both debit and credit)
        PageNumber: page,
        PageSize: pageSize,
        BusinessId: filterData.BusinessId || session.businessId,
        BranchId: filterData.BranchId || session.branchId,
      };

      const result = await getCashLedgers(filterPayload);

      const entries = result.cashLedgers || [];
      setCashLedgers(entries);
      setTotalCount(result.totalCount || 0);
      setTotalPages(result.totalPages || 0);

      // Check day closing status for each entry (only if businessId is available)
      if (session.businessId && entries.length > 0) {
        checkDayClosingForEntries(entries);
      }
    } catch (error) {
      console.error("Error loading cash ledger entries:", error);
      Swal.fire("Error", "Failed to load cash ledger entries", "error");
    } finally {
      setLoading(false);
    }
  };

  // Check day closing status for entries
  const checkDayClosingForEntries = async (entries) => {
    if (!session.businessId) return;

    const statusMap = {};

    // Check unique dates only
    const uniqueDates = [...new Set(entries.map(entry => {
      const date = entry.date || entry.Date;
      return date ? new Date(date).toISOString().split('T')[0] : null;
    }).filter(Boolean))];

    try {
      const checkPromises = uniqueDates.map(async (dateStr) => {
        try {
          const checkRequest = {
            TransactionDate: dateStr,
            BusinessId: session.businessId,
            BranchId: session.branchId,
          };
          const result = await checkDayClosing(checkRequest);
          return { date: dateStr, isClosed: result.isClosed || false, isHoliday: result.isHoliday || false };
        } catch (error) {
          console.error(`Error checking day closing for ${dateStr}:`, error);
          return { date: dateStr, isClosed: false, isHoliday: false };
        }
      });

      const results = await Promise.all(checkPromises);
      results.forEach(({ date, isClosed, isHoliday }) => {
        statusMap[date] = { isClosed, isHoliday };
      });

      setDayClosingStatus(statusMap);
    } catch (error) {
      console.error("Error checking day closing status:", error);
    }
  };

  // Check if entry date is closed
  const isEntryDateClosed = (entry) => {
    if (!session.businessId) return false;
    const date = entry.date || entry.Date;
    if (!date) return false;
    const dateStr = new Date(date).toISOString().split('T')[0];
    const status = dayClosingStatus[dateStr];
    return status ? (status.isClosed || status.isHoliday) : false;
  };

  // Handle edit button click
  const handleEditClick = async (entry) => {
    try {
      const cashLedgerId = entry.cashLedgerID || entry.CashLedgerID;
      const entryDate = entry.date || entry.Date;

      // Get business/branch from entry or session
      const entryBusinessId = entry.businessId || entry.BusinessId || session.businessId;
      const entryBranchId = entry.branchId || entry.BranchId || session.branchId;

      // MANDATORY: BusinessId is required for cash ledger operations
      if (!entryBusinessId) {
        Swal.fire({
          icon: 'error',
          title: 'Business ID Required',
          text: 'Business ID is mandatory for editing petty cash entries. Please contact your administrator to assign a business to your account.',
        });
        return;
      }

      // Check day closing before allowing edit
      const dateStr = new Date(entryDate).toISOString().split('T')[0];
      const checkRequest = {
        TransactionDate: dateStr,
        BusinessId: entryBusinessId,
        BranchId: entryBranchId,
      };

      const dayClosingCheck = await checkDayClosing(checkRequest);
      if (dayClosingCheck.isClosed || dayClosingCheck.isHoliday) {
        const message = dayClosingCheck.isHoliday
          ? "Cannot edit petty cash entry. This date is marked as a holiday."
          : "Cannot edit petty cash entry. Day closing has been marked for this date.";
        Swal.fire("Error", message, "error");
        return;
      }

      // Load entry details
      setEditLoading(true);
      const entryData = await getCashLedgerById(cashLedgerId);

      if (!entryData) {
        Swal.fire("Error", "Entry not found", "error");
        return;
      }

      const debit = entryData.debit || entryData.Debit || 0;
      const credit = entryData.credit || entryData.Credit || 0;
      const amount = debit > 0 ? debit : credit;
      const isReceipt = debit > 0;

      // Get business/branch from entryData (prefer entryData over initial entry values)
      const finalBusinessId = entryData.businessId || entryData.BusinessId || entryBusinessId;
      const finalBranchId = entryData.branchId || entryData.BranchId || entryBranchId;

      setEditingEntry(entryData);
      setEditForm({
        Date: new Date(entryDate),
        Amount: amount,
        TransactorType: entryData.transactorType || entryData.TransactorType,
        TransactorId: entryData.transactorId || entryData.TransactorId,
        CostHeadId: entryData.costHeadId || entryData.CostHeadId || null,
        Notes: entryData.notes || entryData.Notes || "",
        BusinessId: finalBusinessId || session.businessId,
        BranchId: finalBranchId || session.branchId,
      });

      // Load transactor options based on type
      const transactorType = entryData.transactorType || entryData.TransactorType;
      if (transactorType === 'ba') {
        // For banks, we need to find the bank account and its bank
        const bankAccount = bankAccounts.find(acc =>
          (acc.bankAccountId || acc.BankAccountId || acc.bankAccount_Id || acc.BankAccount_Id) == entryData.transactorId
        );
        if (bankAccount) {
          const bankId = bankAccount.bankId || bankAccount.BankId || bankAccount.bank_Id || bankAccount.Bank_Id;
          if (bankId) {
            const bank = banks.find(b => (b.bankId || b.BankId || b.bank_Id || b.Bank_Id) == bankId);
            if (bank) {
              setSelectedBank({
                value: bankId,
                label: bank.bankNameEn || bank.BankNameEn || bank.bank_NameEn || bank.Bank_NameEn || bank.bank_Name || bank.Bank_Name || '',
              });
              loadBankAccountsForEdit(bankId);
            }
          }
        }
      } else {
        loadTransactorsForEdit(transactorType);
      }

      setShowEditModal(true);
    } catch (error) {
      console.error("Error loading entry for edit:", error);
      Swal.fire("Error", "Failed to load entry for editing", "error");
    } finally {
      setEditLoading(false);
    }
  };

  // Load transactors for edit
  const loadTransactorsForEdit = async (transactorType) => {
    try {
      setLoadingTransactors(true);
      setTransactorOptions([]);

      switch (transactorType) {
        case 'ba':
          const banksArray = Array.isArray(banks) ? banks : [];
          const bankOptions = banksArray.map(b => ({
            value: b.bankId || b.BankId || b.bank_Id || b.Bank_Id,
            label: b.bankNameEn || b.BankNameEn || b.bank_NameEn || b.Bank_NameEn || b.bank_Name || b.Bank_Name || '',
          }));
          setTransactorOptions(bankOptions);
          break;
        case 'em':
          const employeesArray = Array.isArray(employees) ? employees : [];
          const employeeOptions = employeesArray.map(emp => ({
            value: emp.employee_Id || emp.Employee_Id || emp.employeeId || emp.EmployeeId,
            label: `${emp.employee_Code || emp.Employee_Code || emp.employeeCode || emp.EmployeeCode || ''} - ${emp.employee_NameEn || emp.Employee_NameEn || emp.employeeNameEn || emp.EmployeeNameEn || ''}`.trim(),
          }));
          setTransactorOptions(employeeOptions);
          break;
        case 'su':
          const suppliersArray = Array.isArray(suppliers) ? suppliers : [];
          const supplierOptions = suppliersArray.map(sup => ({
            value: sup.supplier_Id || sup.Supplier_Id || sup.supplierId || sup.SupplierId,
            label: sup.supplier_NameEn || sup.Supplier_NameEn || sup.supplierNameEn || sup.SupplierNameEn || sup.supplier_Name || sup.Supplier_Name || '',
          }));
          setTransactorOptions(supplierOptions);
          break;
        case 'ot':
          const otherPayeesArray = Array.isArray(otherPayees) ? otherPayees : [];
          const otherPayeeOptions = otherPayeesArray.map(op => ({
            value: op.otherPayee_Id || op.OtherPayee_Id || op.otherPayeeId || op.OtherPayeeId,
            label: op.otherPayee_Name || op.OtherPayee_Name || op.otherPayeeName || op.OtherPayeeName || '',
          }));
          setTransactorOptions(otherPayeeOptions);
          break;
        default:
          setTransactorOptions([]);
      }
    } catch (error) {
      console.error("Error loading transactors:", error);
    } finally {
      setLoadingTransactors(false);
    }
  };

  // Load bank accounts for edit
  const loadBankAccountsForEdit = async (bankId) => {
    try {
      setLoadingTransactors(true);
      const accountsResponse = await getBankAccounts({ bankId: bankId });
      const accountsArray = accountsResponse?.bankAccounts || accountsResponse || [];
      const accountOptions = accountsArray.map(acc => {
        const accountHolderName = acc.accountHolderName || acc.AccountHolderName || '';
        const accountNumber = acc.accountNumber || acc.AccountNumber || '';
        const label = accountHolderName && accountNumber
          ? `${accountHolderName} - ${accountNumber}`
          : accountHolderName || accountNumber || acc.bankAccount_Name || acc.BankAccount_Name || '';

        return {
          value: acc.bankAccountId || acc.BankAccountId || acc.bankAccount_Id || acc.BankAccount_Id,
          label: label,
        };
      });
      setBankAccountsForEdit(accountOptions);
      setTransactorOptions(accountOptions);
    } catch (error) {
      console.error("Error loading bank accounts:", error);
    } finally {
      setLoadingTransactors(false);
    }
  };

  // Handle edit form change
  const handleEditFormChange = (field, value) => {
    if (field === 'TransactorType') {
      setEditForm(prev => ({
        ...prev,
        [field]: value,
        TransactorId: null
      }));
      setSelectedBank(null);
      if (value) {
        loadTransactorsForEdit(value);
      } else {
        setTransactorOptions([]);
      }
    } else if (field === 'TransactorId') {
      setEditForm(prev => ({ ...prev, [field]: value }));
    } else {
      setEditForm(prev => ({ ...prev, [field]: value }));
    }
  };

  // Handle bank change in edit
  const handleBankChangeForEdit = (option) => {
    setSelectedBank(option);
    if (option) {
      loadBankAccountsForEdit(option.value);
    } else {
      setBankAccountsForEdit([]);
      setTransactorOptions([]);
      setEditForm(prev => ({ ...prev, TransactorId: null }));
    }
  };

  // Handle save edit
  const handleSaveEdit = async () => {
    try {
      // Get BusinessId from entry or session
      const businessId = editForm.BusinessId || session.businessId;
      const branchId = editForm.BranchId || session.branchId;

      // MANDATORY: BusinessId is required for cash ledger operations
      if (!businessId) {
        Swal.fire({
          icon: 'error',
          title: 'Business ID Required',
          text: 'Business ID is mandatory for saving petty cash entries. Please contact your administrator to assign a business to your account.',
        });
        return;
      }

      // Validation
      if (!editForm.Date) {
        Swal.fire("Error", "Please select a date", "error");
        return;
      }
      if (!editForm.Amount || editForm.Amount <= 0) {
        Swal.fire("Error", "Please enter a valid amount", "error");
        return;
      }
      if (!editForm.TransactorType) {
        Swal.fire("Error", "Please select a transactor type", "error");
        return;
      }
      if (!editForm.TransactorId) {
        Swal.fire("Error", "Please select a transactor", "error");
        return;
      }

      // Check day closing before save
      const dateStr = new Date(editForm.Date).toISOString().split('T')[0];
      const checkRequest = {
        TransactionDate: dateStr,
        BusinessId: businessId,
        BranchId: branchId,
      };

      const dayClosingCheck = await checkDayClosing(checkRequest);
      if (dayClosingCheck.isClosed || dayClosingCheck.isHoliday) {
        const message = dayClosingCheck.isHoliday
          ? "Cannot save changes. This date is marked as a holiday."
          : "Cannot save changes. Day closing has been marked for this date.";
        Swal.fire("Error", message, "error");
        return;
      }

      // Also check original date if changed
      const originalDate = editingEntry.date || editingEntry.Date;
      const originalDateStr = new Date(originalDate).toISOString().split('T')[0];
      if (dateStr !== originalDateStr) {
        const originalCheckRequest = {
          TransactionDate: originalDateStr,
          BusinessId: businessId,
          BranchId: branchId,
        };
        const originalDayClosingCheck = await checkDayClosing(originalCheckRequest);
        if (originalDayClosingCheck.isClosed || originalDayClosingCheck.isHoliday) {
          const message = originalDayClosingCheck.isHoliday
            ? "Cannot change date. Original date is marked as a holiday."
            : "Cannot change date. Original date is closed.";
          Swal.fire("Error", message, "error");
          return;
        }
      }

      setEditLoading(true);

      const cashLedgerId = editingEntry.cashLedgerID || editingEntry.CashLedgerID;
      const originalDebit = editingEntry.debit || editingEntry.Debit || 0;
      const originalCredit = editingEntry.credit || editingEntry.Credit || 0;
      const isReceipt = originalDebit > 0;

      const cashLedgerData = {
        CashLedgerID: cashLedgerId,
        SlNo: editingEntry.slNo || editingEntry.SlNo || 0,
        Date: formatDateForAPI(editForm.Date, true),
        Debit: isReceipt ? editForm.Amount : null,
        Credit: !isReceipt ? editForm.Amount : null,
        Notes: editForm.Notes || null,
        TransactorType: editForm.TransactorType,
        TransactorId: editForm.TransactorId,
        CostHeadId: editForm.CostHeadId || null,
        BusinessId: businessId,
        BranchId: branchId,
        ModifiedBy: session.userId,
      };

      const result = await saveCashLedger(cashLedgerData);

      if (result.isSuccess) {
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: result.message || 'Cash ledger entry updated successfully',
        });

        setShowEditModal(false);
        setEditingEntry(null);
        setEditForm({
          Date: new Date(),
          Amount: 0,
          TransactorType: null,
          TransactorId: null,
          CostHeadId: null,
          Notes: "",
          BusinessId: null,
          BranchId: null,
        });
        setSelectedBank(null);
        setBankAccountsForEdit([]);
        setTransactorOptions([]);

        // Reload entries
        loadCashLedgers();
      } else {
        Swal.fire("Error", result.message || "Failed to update cash ledger entry", "error");
      }
    } catch (error) {
      console.error("Error saving cash ledger:", error);
      const errorMessage = error.response?.data?.message || error.message || "An error occurred while updating the cash ledger entry";
      Swal.fire("Error", errorMessage, "error");
    } finally {
      setEditLoading(false);
    }
  };

  // Check if entry can be edited (BusinessId must exist)
  const canEditEntry = (entry) => {
    // BusinessId is mandatory - if not present, cannot edit
    if (!session.businessId) {
      return false;
    }
    // Also check if date is closed
    return !isEntryDateClosed(entry);
  };

  const handleFilterChange = (field, value) => {
    if (field === 'BusinessId') {
      setFilter(prev => ({ ...prev, [field]: value, BranchId: null }));
    } else {
      setFilter(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleApplyFilter = () => {
    setPage(1);
    loadCashLedgers(filter);
  };

  const handleClearFilter = () => {
    setFilter(emptyFilter);
    setPage(1);
    loadCashLedgers(emptyFilter);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleExportToExcel = async () => {
    try {
      setExporting(true);

      // Prepare filter for export (no pagination)
      const exportFilter = {
        FromDate: filter.FromDate,
        ToDate: filter.ToDate,
        TransactorType: filter.TransactorType,
        TransactorId: filter.TransactorId,
        BusinessId: filter.BusinessId || session.businessId,
        BranchId: filter.BranchId || session.branchId,
        FilterType: null, // Get all entries (both debit and credit)
      };

      await exportCashLedgerToExcel(exportFilter);
      Swal.fire("Success", "Petty cash report exported successfully", "success");
    } catch (error) {
      console.error("Error exporting petty cash report:", error);
      Swal.fire("Error", "Failed to export petty cash report", "error");
    } finally {
      setExporting(false);
    }
  };

  const getTransactorTypeLabel = (type) => {
    if (!type) return '-';
    const option = transactorTypeOptions.find(opt => opt.value === type);
    return option ? option.label : type;
  };

  const getTransactorName = (transactorType, transactorId) => {
    if (!transactorType || !transactorId) return '-';

    try {
      switch (transactorType) {
        case 'ba':
          // For banks, transactorId is bankAccountId
          const bankAccount = bankAccounts.find(acc =>
            (acc.bankAccountId || acc.BankAccountId || acc.bankAccount_Id || acc.BankAccount_Id) == transactorId
          );
          if (bankAccount) {
            const accountHolderName = bankAccount.accountHolderName || bankAccount.AccountHolderName || '';
            const accountNumber = bankAccount.accountNumber || bankAccount.AccountNumber || '';
            return accountHolderName && accountNumber
              ? `${accountHolderName} - ${accountNumber}`
              : accountHolderName || accountNumber || '-';
          }
          return '-';

        case 'ch':
          const costHead = costHeads.find(ch =>
            (ch.costHead_Id || ch.CostHead_Id) == transactorId
          );
          return costHead ? (costHead.costHead_Name || costHead.CostHead_Name || '-') : '-';

        case 'em':
          const employee = employees.find(emp =>
            (emp.employee_Id || emp.Employee_Id || emp.employeeId || emp.EmployeeId) == transactorId
          );
          if (employee) {
            const name = employee.employee_NameEn || employee.Employee_NameEn || employee.employeeNameEn || employee.EmployeeNameEn || '';
            return name || '-';
          }
          return '-';

        case 'su':
          const supplier = suppliers.find(sup =>
            (sup.supplier_Id || sup.Supplier_Id || sup.supplierId || sup.SupplierId) == transactorId
          );
          return supplier ? (supplier.supplier_NameEn || supplier.Supplier_NameEn || supplier.supplierNameEn || supplier.SupplierNameEn || supplier.supplier_Name || supplier.Supplier_Name || '-') : '-';

        case 'ot':
          const otherPayee = otherPayees.find(op =>
            (op.otherPayee_Id || op.OtherPayee_Id || op.otherPayeeId || op.OtherPayeeId) == transactorId
          );
          return otherPayee ? (otherPayee.otherPayee_Name || otherPayee.OtherPayee_Name || otherPayee.otherPayeeName || otherPayee.OtherPayeeName || '-') : '-';

        default:
          return '-';
      }
    } catch (error) {
      console.error("Error getting transactor name:", error);
      return '-';
    }
  };

  const formatCurrency = (amount) => {
    return parseFloat(amount || 0).toFixed(2);
  };

  const getCostHeadName = (costHeadId) => {
    if (!costHeadId) return '-';
    const costHead = costHeads.find(ch =>
      (ch.costHead_Id || ch.CostHead_Id) == costHeadId
    );
    return costHead ? (costHead.costHead_Name || costHead.CostHead_Name || '-') : '-';
  };

  // Calculate totals
  const totals = useMemo(() => {
    const totalDebit = cashLedgers.reduce((sum, entry) => {
      const debit = entry.debit || entry.Debit || 0;
      return sum + parseFloat(debit);
    }, 0);

    const totalCredit = cashLedgers.reduce((sum, entry) => {
      const credit = entry.credit || entry.Credit || 0;
      return sum + parseFloat(credit);
    }, 0);

    return {
      totalDebit,
      totalCredit,
      balance: totalDebit - totalCredit
    };
  }, [cashLedgers]);

  // Pagination component
  const Pagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      pages.push(i);
    }

    return (
      <div className="d-flex justify-content-between align-items-center mt-3">
        <div>
          Showing {((page - 1) * pageSize) + 1} to {Math.min(page * pageSize, totalCount)} of {totalCount} entries
        </div>
        <div className="d-flex gap-1">
          <Button
            color="primary"
            size="sm"
            disabled={page === 1}
            onClick={() => handlePageChange(page - 1)}
          >
            Previous
          </Button>
          {pages.map(pageNum => (
            <Button
              key={pageNum}
              color={pageNum === page ? "primary" : "outline-primary"}
              size="sm"
              onClick={() => handlePageChange(pageNum)}
            >
              {pageNum}
            </Button>
          ))}
          <Button
            color="primary"
            size="sm"
            disabled={page === totalPages}
            onClick={() => handlePageChange(page + 1)}
          >
            Next
          </Button>
        </div>
      </div>
    );
  };

  if (!hasPermission) {
    return null;
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumb title="Petty Cash Report" breadcrumbItem="Petty Cash Report" />

        <Row>
          <Col xs={12}>
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h4 className="card-title mb-0">Petty Cash Report</h4>
                  <div className="d-flex gap-2">
                    <Button
                      color="success"
                      onClick={handleExportToExcel}
                      disabled={exporting || loading || cashLedgers.length === 0}
                    >
                      <Download className="me-1" size={16} />
                      {exporting ? "Exporting..." : "Export to Excel"}
                    </Button>
                    <Button
                      color="info"
                      onClick={() => setShowFilters(!showFilters)}
                    >
                      {showFilters ? "Hide Filters" : "Show Filters"}
                    </Button>
                  </div>
                </div>

                {/* Filters */}
                {showFilters && (
                  <Card className="mb-4">
                    <CardBody>
                      <Row>
                        <Col md={3}>
                          <FormGroup>
                            <Label>Company</Label>
                            <Select
                              value={businesses.find(b => (b.business_Id || b.businessId) === filter.BusinessId)
                                ? {
                                  value: filter.BusinessId,
                                  label: businesses.find(b => (b.business_Id || b.businessId) === filter.BusinessId)?.business_NameEn ||
                                    businesses.find(b => (b.business_Id || b.businessId) === filter.BusinessId)?.business_Name || ''
                                }
                                : null}
                              onChange={(option) => handleFilterChange("BusinessId", option?.value || null)}
                              options={businesses.map(b => ({
                                value: b.business_Id || b.businessId,
                                label: b.business_NameEn || b.business_Name || b.businessNameEn || ''
                              }))}
                              isClearable={!session.businessId}
                              isDisabled={loadingBusinesses || !!session.businessId}
                              placeholder="Select company"
                              styles={selectStyles}
                              isLoading={loadingBusinesses}
                            />
                          </FormGroup>
                        </Col>
                        <Col md={3}>
                          <FormGroup>
                            <Label>Branch</Label>
                            <Select
                              value={filterBranches.find(b => (b.branch_Id || b.branchId) === filter.BranchId)
                                ? {
                                  value: filter.BranchId,
                                  label: (() => {
                                    const branch = filterBranches.find(b => (b.branch_Id || b.branchId) === filter.BranchId);
                                    return branch ? `${branch.branch_NameEn || branch.branch_Name || branch.branchNameEn || ''} - ${branch.branch_NameAr || branch.branchNameAr || ''}`.trim() : '';
                                  })()
                                }
                                : null}
                              onChange={(option) => handleFilterChange("BranchId", option?.value || null)}
                              options={filterBranches.map(b => ({
                                value: b.branch_Id || b.branchId,
                                label: `${b.branch_NameEn || b.branch_Name || b.branchNameEn || ''} - ${b.branch_NameAr || b.branchNameAr || ''}`.trim()
                              }))}
                              isClearable={!session.branchId}
                              isDisabled={loadingBranches || !filter.BusinessId || !!session.branchId}
                              placeholder={filter.BusinessId ? "Select branch" : "Select business first"}
                              styles={selectStyles}
                              isLoading={loadingBranches}
                            />
                          </FormGroup>
                        </Col>
                        <Col md={3}>
                          <FormGroup>
                            <Label>From Date</Label>
                            <DatePicker
                              selected={filter.FromDate}
                              onChange={(date) => handleFilterChange("FromDate", date)}
                              dateFormat="dd/MM/yyyy"
                              className="form-control"
                              isClearable
                              placeholderText="Select from date"
                            />
                          </FormGroup>
                        </Col>
                        <Col md={3}>
                          <FormGroup>
                            <Label>To Date</Label>
                            <DatePicker
                              selected={filter.ToDate}
                              onChange={(date) => handleFilterChange("ToDate", date)}
                              dateFormat="dd/MM/yyyy"
                              className="form-control"
                              isClearable
                              placeholderText="Select to date"
                            />
                          </FormGroup>
                        </Col>
                      </Row>
                      <Row>
                        <Col md={3}>
                          <FormGroup>
                            <Label>Transactor Type</Label>
                            <Select
                              value={transactorTypeOptions.find(opt => opt.value === filter.TransactorType) || null}
                              onChange={(option) => handleFilterChange("TransactorType", option?.value || null)}
                              options={transactorTypeOptions}
                              isClearable
                              placeholder="Select transactor type"
                              styles={selectStyles}
                            />
                          </FormGroup>
                        </Col>
                        <Col md={3}>
                          <div className="d-flex gap-2 align-items-end h-100">
                            <Button color="primary" onClick={handleApplyFilter}>
                              Apply
                            </Button>
                            <Button color="warning" onClick={handleClearFilter}>
                              Clear
                            </Button>
                          </div>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>
                )}

                {/* Table */}
                {loading ? (
                  <div className="text-center py-5">
                    <ClipLoader size={50} color="#556ee6" />
                  </div>
                ) : (
                  <>
                    <div className="table-responsive">
                      <Table bordered>
                        <thead className="table-light">
                          <tr>
                            <th>Sr.No</th>
                            <th>Date</th>
                            <th>Debit</th>
                            <th>Credit</th>
                            <th>Transactor Type</th>
                            <th>Transactor</th>
                            <th>Cost Head</th>
                            <th>Notes</th>
                            {hasEditOrHigher && !isViewOnly && <th>Action</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {cashLedgers.length === 0 ? (
                            <tr>
                              <td colSpan={hasEditOrHigher && !isViewOnly ? 9 : 8} className="text-center">
                                No transactions found
                              </td>
                            </tr>
                          ) : (
                            cashLedgers.map((entry, index) => {
                              // Handle both camelCase and PascalCase property names
                              const cashLedgerId = entry.cashLedgerID || entry.CashLedgerID;
                              const date = entry.date || entry.Date;
                              const debit = entry.debit || entry.Debit || 0;
                              const credit = entry.credit || entry.Credit || 0;
                              const transactorType = entry.transactorType || entry.TransactorType;
                              const transactorId = entry.transactorId || entry.TransactorId;
                              const costHeadId = entry.costHeadId || entry.CostHeadId;
                              const notes = entry.notes || entry.Notes;
                              const canEdit = canEditEntry(entry);
                              const isDateClosed = isEntryDateClosed(entry);

                              return (
                                <tr key={cashLedgerId}>
                                  <td>{(page - 1) * pageSize + index + 1}</td>
                                  <td>{formatDateForDisplay(date, 'date')}</td>
                                  <td>{formatCurrency(debit)}</td>
                                  <td>{formatCurrency(credit)}</td>
                                  <td>{getTransactorTypeLabel(transactorType)}</td>
                                  <td>{getTransactorName(transactorType, transactorId)}</td>
                                  <td>{getCostHeadName(costHeadId)}</td>
                                  <td>{notes || '-'}</td>
                                  {hasEditOrHigher && !isViewOnly && (
                                    <td>
                                      {canEdit ? (
                                        <Button
                                          color="info"
                                          size="sm"
                                          onClick={() => handleEditClick(entry)}
                                          disabled={editLoading}
                                        >
                                          <Edit size={14} className="me-1" />
                                          Edit
                                        </Button>
                                      ) : (
                                        <span className="text-muted" style={{ fontSize: '12px' }}>
                                          {!session.businessId ? 'Business ID Required' : isDateClosed ? 'Closed' : 'N/A'}
                                        </span>
                                      )}
                                    </td>
                                  )}
                                </tr>
                              );
                            })
                          )}
                          {/* Totals Row */}
                          {cashLedgers.length > 0 && (
                            <tr className="table-info fw-bold">
                              <td colSpan={hasEditOrHigher && !isViewOnly ? 3 : 2} className="text-end">Total:</td>
                              <td>{formatCurrency(totals.totalDebit)}</td>
                              <td>{formatCurrency(totals.totalCredit)}</td>
                              <td colSpan={hasEditOrHigher && !isViewOnly ? 4 : 3} className="text-end">Balance:</td>
                              <td className={totals.balance >= 0 ? 'text-success' : 'text-danger'}>
                                {formatCurrency(totals.balance)}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </div>

                    <Pagination />
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Edit Modal */}
        <Modal isOpen={showEditModal} toggle={() => !editLoading && setShowEditModal(false)} size="lg">
          <ModalHeader toggle={() => !editLoading && setShowEditModal(false)}>
            Edit Petty Cash Entry
          </ModalHeader>
          <ModalBody>
            {editLoading && !editingEntry ? (
              <div className="text-center py-4">
                <ClipLoader size={40} color="#556ee6" />
                <p className="mt-2">Loading entry details...</p>
              </div>
            ) : (
              <Form>
                <Row>
                  <Col md={6}>
                    <FormGroup className="mb-3">
                      <Label className="mb-2">Date <span className="text-danger">*</span></Label>
                      <DatePicker
                        selected={editForm.Date}
                        onChange={(date) => handleEditFormChange("Date", date)}
                        dateFormat="dd/MM/yyyy"
                        className="form-control"
                        maxDate={new Date()}
                        disabled={editLoading}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup className="mb-3">
                      <Label className="mb-2">Amount <span className="text-danger">*</span></Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={editForm.Amount}
                        onChange={(e) => handleEditFormChange("Amount", parseFloat(e.target.value) || 0)}
                        disabled={editLoading}
                        placeholder="Enter amount"
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <FormGroup className="mb-3">
                      <Label className="mb-2">Transactor Type <span className="text-danger">*</span></Label>
                      <Select
                        value={transactorTypeOptions.find(opt => opt.value === editForm.TransactorType) || null}
                        onChange={(option) => handleEditFormChange("TransactorType", option?.value || null)}
                        options={transactorTypeOptions}
                        isClearable
                        isDisabled={editLoading || loadingTransactors}
                        placeholder="Select transactor type"
                        styles={selectStyles}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup className="mb-3">
                      <Label className="mb-2">Transactor Selection <span className="text-danger">*</span></Label>
                      {editForm.TransactorType === 'ba' && !selectedBank ? (
                        <Select
                          value={transactorOptions.find(opt => opt.value === editForm.TransactorId) || null}
                          onChange={handleBankChangeForEdit}
                          options={transactorOptions}
                          isClearable
                          isDisabled={editLoading || loadingTransactors}
                          placeholder="Select bank"
                          isLoading={loadingTransactors}
                          styles={selectStyles}
                        />
                      ) : (
                        <Select
                          value={transactorOptions.find(opt => opt.value === editForm.TransactorId) || null}
                          onChange={(option) => handleEditFormChange("TransactorId", option?.value || null)}
                          options={transactorOptions}
                          isClearable
                          isDisabled={editLoading || loadingTransactors || (editForm.TransactorType === 'ba' && !selectedBank)}
                          placeholder={
                            editForm.TransactorType === 'ba' && !selectedBank
                              ? "Select bank first"
                              : editForm.TransactorType === 'ba' && selectedBank
                                ? "Select bank account"
                                : "Select transactor"
                          }
                          isLoading={loadingTransactors}
                          styles={selectStyles}
                        />
                      )}
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <FormGroup className="mb-3">
                      <Label className="mb-2">Cost Head</Label>
                      <Select
                        value={costHeads.find(ch => (ch.costHead_Id || ch.CostHead_Id) === editForm.CostHeadId)
                          ? {
                            value: editForm.CostHeadId,
                            label: costHeads.find(ch => (ch.costHead_Id || ch.CostHead_Id) === editForm.CostHeadId)?.costHead_Name ||
                              costHeads.find(ch => (ch.costHead_Id || ch.CostHead_Id) === editForm.CostHeadId)?.CostHead_Name || ''
                          }
                          : null}
                        onChange={(option) => handleEditFormChange("CostHeadId", option?.value || null)}
                        options={costHeads.map(ch => ({
                          value: ch.costHead_Id || ch.CostHead_Id,
                          label: ch.costHead_Name || ch.CostHead_Name || '',
                        }))}
                        isClearable
                        isDisabled={editLoading}
                        placeholder="Select cost head"
                        styles={selectStyles}
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col md={12}>
                    <FormGroup className="mb-3">
                      <Label className="mb-2">Notes</Label>
                      <Input
                        type="textarea"
                        rows="3"
                        value={editForm.Notes}
                        onChange={(e) => handleEditFormChange("Notes", e.target.value)}
                        disabled={editLoading}
                        placeholder="Enter notes"
                      />
                    </FormGroup>
                  </Col>
                </Row>
              </Form>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              color="secondary"
              onClick={() => setShowEditModal(false)}
              disabled={editLoading}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              onClick={handleSaveEdit}
              disabled={editLoading}
            >
              {editLoading ? (
                <>
                  <ClipLoader size={15} color="#fff" className="me-2" />
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    </div>
  );
}

